import { apiService } from "@/_services";
import { derivativeImage } from "@/_core/charts/D3ChartAnalyticsChartHelpers";
import { FilterMatchMode, FilterOperator } from "primevue/api/";
import {
  nest,
  sum,
  timeDay,
  timeMonday,
  timeMonth,
  timeParse,
  timeYear,
  values
} from "d3";
import {
  dateRangeToString,
  formatDate_YMD,
  longShortTransform,
  sortByKey,
  timeParseNoNull,
  timeParseNoNull2,
  todayWithoutTime
} from "@/_core/_helpers/_helpers";
import { formatPercentSm } from "@/_core/_helpers/_parametric_tools";
import { add, nonNans } from "@/_core/data/Advanced";
import { longShort } from "../charts/D3ChartAnalyticsChartHelpers";

/**
 * Constructs an Analytics object to manage user preferences and selections.
 *
 * @param {object} analyticsPreferences - User preferences from $store.
 * @param {string} assetType - Either "commodity" or "currency".
 * @param {object} companies - User's associated companies.
 * @param {object} companyParameters - Company-specific parameters (plants, end-products, business, etc.).
 * @param {object} images - Derivative images.
 * @param {object} processData - Shared process class.
 * @param {object} underlyingSpecs - Specifications for commodities or currencies.
 */
export const Analytics = function(
  analyticsPreferences,
  assetType,
  companies,
  companyParameters,
  images,
  processData,
  underlyingSpecs
) {
  this.analyticsPreferences = analyticsPreferences;
  this.assetType = assetType;
  this.companies = companies;
  this.companyParameters = companyParameters;
  this.images = images;
  this.optionTypes = [];
  this.processData = processData;
  this.underlyingSpecs = underlyingSpecs;

  // Loading states
  this.loading = Object.fromEntries(
    [
      "accounting",
      "budget",
      "expoFuture",
      "expoPast",
      "hedgesFuture",
      "hedgesPast",
      "histCandles",
      "pastCalcs",
      "portCalcs",
      "program",
      "spot",
      "strategies",
      "derivatives",
      "topographyCalcs",
      "topographyCalcsRates",
      "valuationFinalSpot",
      "valuationInitialSpot"
    ].map(key => [key, false])
  );

  // Raw data structure
  this.rawData = {
    budget: [],
    dataFilters: defineFilters(),
    dDowns: defineDDowns(),
    totals: defineTotals(),
    existLFwd: false,
    existSFwd: false,
    existLCall: false,
    existSCall: false,
    existLPut: false,
    existSPut: false,
    expoFuture: [],
    expoPast: [],
    filteredData: [],
    hedgesFuture: [],
    hedgesPast: [],
    historicalData: [],
    historicalOHLC: {},
    mergedExpoHedges: [],
    nestedData: [],
    toBeFetched: true,
    toBeFetchedHidden: false,
    program: [],
    render: true,
    years: {},
    zoomedData: [],
    strategies: [],
    derivatives: [],
    zoomedDerivatives: []
  };

  // Build underlying object map
  this.underlyingSpecs = underlyingSpecs;
  this.underlyingObject = underlyingSpecs.reduce((obj, spec) => {
    obj[spec.pk_idcommodity] = spec;
    return obj;
  }, {});

  // User selections
  this.userSelections = {
    assets: [
      { product_name: "N.A.", fk_idcurrency: "N.A.", fk_idunit: "N.A." }
    ],
    assetsNums: [],
    bucket: "MONTHLY",
    expandSidebar: false,
    fldBudget: "budgetP",
    fldFixed: "fixedP",
    fldFixedPerc: "fixedPPerc",
    fldMkt: "marketP",
    fldMktPerc: "marketPPerc",
    fldNet: "netP",
    fldNetPerc: "netPPerc",
    includeFutures: true,
    includeOtherCosts: true,
    includeRatioBasis: true,
    intervalGroup: "1M",
    normalizeQty: false,
    portfolioNums: [],
    portfolios: [],
    selectedAnalysis: "scenarios",
    selectedAnalysisParent: "scenario",
    selectedAsset: [
      {
        product_name: "N.A.",
        fk_idcurrency: "N.A.",
        fk_idunit: "N.A.",
        currency_name: "N.A/N.A"
      }
    ],
    selectedCompanies: [],
    selectedCurrencies: [],
    selectedPortfolios: [],
    selectedUnderlying: [{ commodity_name: "N.A." }],
    selectionStr: "",
    showAll: false,
    showAve: false,
    showBudget: true,
    showForecast: true,
    showDetails: false,
    showFixedBasis: true,
    showFixedFutures: true,
    showLCall: false,
    showLFwd: false,
    showLPut: false,
    showMarket: true,
    showNet: true,
    showSCall: false,
    showSFwd: false,
    showSimulations: false,
    showSPut: false,
    underlying: [{ commodity_name: "N.A." }],
    underlyingNums: [],
    visibleLeft: false,
    xZoom: [],
    year: null
  };

  // Valuation Data
  this.valuation = {
    data: defineEmptyValTable(),
    dataFilters: defineFilters(),
    dDowns: defineDDowns(),
    finalDate: null,
    finalSpot: null,
    initialDate: null,
    initialSpot: null,
    toBeFetched: true,
    valuated: false
  };
};

/**
 * Retrieves user Company-Portfolio-Asset Selection from Store or create-saves it
 **/
Analytics.prototype.initStoreCompPortAsset = function() {
  let i;
  let saveUserPref = false;
  let selComp = this.analyticsPreferences.selected_company;
  let selectedCompanies = [];
  if (selComp !== undefined) {
    for (i = 0; i < selComp.length; i++) {
      if (this.companies.find(d => d === selComp[i]) !== undefined) {
        selectedCompanies.splice(i, 0, selComp[i]);
      }
    }
  }
  if (selectedCompanies.length === 0) {
    selectedCompanies.splice(0, 0, this.companies[0]);
    saveUserPref = true;
  }
  Object.assign(this.userSelections, {
    selectedCompanies: selectedCompanies
  });
  let dat;
  if (this.valuation["initialDate"] === null) {
    dat = todayWithoutTime();
    dat.setMonth(dat.getMonth(), 0);
    this.valuation["initialDate"] = dat;
  }
  if (this.valuation["finalDate"] === null) {
    dat = todayWithoutTime();
    // dat.setMonth(dat.getMonth(), 0);
    this.valuation["finalDate"] = dat;
  }
  return this.updateSelectedPortf(true) || saveUserPref;
};

/**
 * On user update Selected Company-Portfolio-Asset
 **/
Analytics.prototype.updateSelectedCompanies = function(companies) {
  Object.assign(this.userSelections, {
    selectedCompanies: companies
  });
  return this.updateSelectedPortf(false);
};

/**
 * On user update Selected Portfolio-Asset
 **/
Analytics.prototype.updateSelectedPortf = function(isInitial = true) {
  let i, saveUserPref;
  // portfolios
  Object.assign(this.userSelections, {
    portfolios: this.getPortfolios(
      this.assetType,
      this.userSelections["selectedCompanies"]
    )
  });
  if (this.userSelections["portfolios"].length === 0) {
    this.userSelections["selectedAsset"] = [
      { product_name: "N.A.", fk_idcurrency: "N.A.", fk_idunit: "N.A." }
    ];
  } else {
    let selPort,
      selectedPortfolios = [];
    if (isInitial) {
      selPort = this.analyticsPreferences.selected_portfolio;
    } else {
      selPort = this.userSelections["selectedPortfolios"];
    }
    let idx;
    for (i = 0; i < selPort.length; i++) {
      idx = this.userSelections["portfolios"].findIndex(
        d => d.name === selPort[i].name
      );
      if (idx > -1) {
        selectedPortfolios.push(this.userSelections["portfolios"][idx]);
      }
    }
    Object.assign(this.userSelections, {
      selectedPortfolios: selectedPortfolios
    });
    if (this.userSelections["selectedPortfolios"].length === 0) {
      Object.assign(this.userSelections, {
        selectedPortfolios: [this.userSelections["portfolios"][0]]
      });
      saveUserPref = true;
    }
    saveUserPref = this.updateSelectedAsset(isInitial) || saveUserPref;
  }
  Object.assign(this.userSelections, {
    portfolioNums: this.userSelections["selectedPortfolios"].map(
      port => port.pk
    )
  });
  saveUserPref = this.getSelectedUnderlying(isInitial) || saveUserPref;
  return saveUserPref;
};

/**
 * On user update Selected Asset for portfolios
 **/
Analytics.prototype.updateSelectedAsset = function(isInitial = true) {
  let _this = this;
  let i,
    saveUserPref = false;
  // End Product for commodity and currency for currency
  let assetPK = this.getAssets(this.userSelections["portfolios"]);
  let assets = [];
  if (this.assetType === "commodity") {
    assetPK.forEach(function(pk) {
      assets.push(
        _this.companyParameters.endProducts.filter(
          d => d.pk_id_end_product === pk
        )[0]
      );
    });
  } else {
    assets = assets.filter(this.processData.onlyUnique);
    assetPK.forEach(function(curr) {
      assets.push(
        _this.underlyingSpecs.filter(d => d.currency_name === curr)[0]
      );
    });
  }
  assets = assets.filter(this.processData.onlyUnique);
  assets = sortByKey(
    assets,
    this.assetType === "commodity" ? "product_name" : "currency_name"
  );
  Object.assign(this.userSelections, { assets: assets });
  if (this.userSelections["assets"].length === 0) {
    this.userSelections["selectedAsset"] =
      this.assetType === "commodity"
        ? [{ product_name: "N.A.", fk_idcurrency: "N.A.", fk_idunit: "N.A." }]
        : [
            {
              currency_name: "EURUSD",
              pk_i_idcurrency: 3,
              fk_idcurrency_right: 0,
              fk_idcurrency_left: 0
            }
          ];
  } else {
    let selAsset,
      selectedAssets = [];
    if (isInitial) {
      if (this.assetType === "commodity") {
        if ("selectedAsset" in this.analyticsPreferences) {
          selAsset = this.analyticsPreferences.selectedAsset;
        } else {
          selAsset = [this.userSelections["assets"][0]];
        }
      } else {
        if ("selectedCurrency" in this.analyticsPreferences) {
          selAsset = this.analyticsPreferences.selectedCurrency;
        } else {
          selAsset = [this.userSelections["assets"][0]];
        }
      }
    } else {
      selAsset = this.userSelections["selectedAsset"];
    }
    let idx;
    for (i = 0; i < selAsset.length; i++) {
      if (this.assetType === "commodity") {
        idx = this.userSelections["assets"].findIndex(
          d => d.pk_id_end_product === selAsset[i].pk_id_end_product
        );
      } else {
        idx = this.userSelections["assets"].findIndex(
          d => d.pk_i_idcurrency === selAsset[i].pk_i_idcurrency
        );
      }
      if (idx > -1) {
        selectedAssets.push(this.userSelections["assets"][idx]);
      }
    }
    if (selectedAssets.length === 0) {
      selectedAssets = [this.userSelections["assets"][0]];
    }
    Object.assign(this.userSelections, {
      selectedAsset: selectedAssets
    });
    if (this.userSelections["selectedAsset"].length === 0) {
      Object.assign(this.userSelections, {
        selectedUnderlying: [this.userSelections["assets"][0]]
      });
      saveUserPref = true;
    }
  }
  Object.assign(this.rawData, {
    budget: [],
    existLFwd: false,
    existSFwd: false,
    existLCall: false,
    existSCall: false,
    existLPut: false,
    existSPut: false,
    expoFuture: [],
    expoPast: [],
    hedgesFuture: [],
    hedgesPast: [],
    historicalData: [],
    historicalOHLC: {},
    toBeFetched: true,
    program: []
  });
  let fieldName =
    this.assetType === "commodity" ? "pk_id_end_product" : "pk_i_idcurrency";
  Object.assign(this.userSelections, {
    assetsNums: this.userSelections["selectedAsset"].map(d => d[fieldName])
  });
  return saveUserPref;
};

/**
 * Retrieves user Company-Portfolio-Underlying Selection from Store or
 * create-saves it
 **/
Analytics.prototype.getSelectedUnderlying = function(isInitial = true) {
  if (this.assetType === "currency") {
    Object.assign(this.userSelections, {
      underlying: [{ commodity_name: "N.A." }]
    });
    return;
  }
  let _this = this;
  let i, saveUserPref;
  let assetPK = this.getUnderlying(this.userSelections["portfolios"]);
  let assets = [];
  assetPK.forEach(function(pk) {
    assets.push(
      _this.underlyingSpecs.filter(
        d => d.fk_idproduct.toUpperCase() === pk.toUpperCase()
      )[0]
    );
  });
  assets = assets.filter(this.processData.onlyUnique);
  assets = sortByKey(assets, "commodity_name");
  Object.assign(this.userSelections, { underlying: assets });
  if (this.userSelections["underlying"].length === 0) {
    this.userSelections["selectedUnderlying"] = [];
  } else {
    let selAsset,
      selectedAssets = [];
    if (isInitial) {
      if ("selectedUnderlying" in this.analyticsPreferences) {
        selAsset = this.analyticsPreferences.selectedUnderlying;
      } else {
        selAsset = [this.userSelections["underlying"][0]];
      }
    } else {
      selAsset = this.userSelections["selectedUnderlying"];
    }
    let idx;
    for (i = 0; i < selAsset.length; i++) {
      idx = this.userSelections["underlying"].findIndex(
        d => d.fk_idproduct === selAsset[i].fk_idproduct
      );
      if (idx > -1) {
        selectedAssets.push(this.userSelections["underlying"][idx]);
      }
    }
    Object.assign(this.userSelections, {
      selectedUnderlying: selectedAssets
    });
    if (this.userSelections["selectedUnderlying"].length === 0) {
      Object.assign(this.userSelections, {
        selectedUnderlying: this.userSelections["underlying"]
      });
      saveUserPref = true;
    }
  }
  Object.assign(this.userSelections, {
    underlyingNums: this.userSelections["selectedUnderlying"].map(
      und => und.pk_idcommodity
    )
  });
  return saveUserPref;
};

/**
 * Retrieves user Portfolio for selected companies
 */
Analytics.prototype.getPortfolios = function(assetType = "currency", comp) {
  let port = [];
  for (let i = 0; i < comp.length; i++) {
    for (let j = 0; j < comp[i].portfolios.length; j++) {
      port.push(this.companies[comp[i].id].portfolios[j]);
    }
  }
  port = port.filter(this.processData.onlyUnique);
  port = sortByKey(port, "portfolio_name");
  return this.assignPortfoliosByAsset(assetType, port);
};

/**
 * Creates available portfolios for companies
 */
Analytics.prototype.assignPortfoliosByAsset = function(assetType, port) {
  let opt = [];
  let fieldAsset = assetType === "currency" ? "fk_currency" : "fk_idcommodity";
  for (let i = 0; i < port.length; i++) {
    if (port[i][fieldAsset].length !== 0) {
      opt.push({
        pk: port[i].pk_idportfolio,
        id: i,
        name: port[i].portfolio_name,
        underlying: port[i][fieldAsset],
        asset: port[i]["fk_id_end_product"]
      });
    }
  }
  opt = opt.filter(this.processData.onlyUnique);
  opt = sortByKey(opt, "name");
  return opt;
};

/**
 * Retrieves assets for selected portfolios
 */
Analytics.prototype.getAssets = function(portfolios) {
  let fieldName = this.assetType === "commodity" ? "asset" : "underlying";
  let opt = [];
  if (this.userSelections["selectedPortfolios"].length > 0) {
    for (let i = 0; i < this.userSelections["selectedPortfolios"].length; i++) {
      for (
        let j = 0;
        j < this.userSelections["selectedPortfolios"][i][fieldName].length;
        j++
      ) {
        opt.push(this.userSelections["selectedPortfolios"][i][fieldName][j]);
      }
    }
  } else {
    if (portfolios.length > 0) {
      opt = portfolios[0][fieldName];
    }
  }
  opt = opt.sort();
  opt = opt.filter(this.processData.onlyUnique);
  return opt;
};

/**
 * Retrieves underlying for selected portfolios
 */
Analytics.prototype.getUnderlying = function(portfolios) {
  let opt = [];
  if (this.userSelections["selectedPortfolios"].length > 0) {
    for (let i = 0; i < this.userSelections["selectedPortfolios"].length; i++) {
      for (
        let j = 0;
        j < this.userSelections["selectedPortfolios"][i]["underlying"].length;
        j++
      ) {
        opt.push(this.userSelections["selectedPortfolios"][i]["underlying"][j]);
      }
    }
  } else {
    if (portfolios.length > 0) {
      opt = portfolios[0]["underlying"];
    }
  }
  opt = opt.sort();
  opt = opt.filter(this.processData.onlyUnique);
  return opt;
};

/**
 * Retrieves user Portfolio for selected companies
 * @param {object} data: data valuated
 */
Analytics.prototype.thenCandleData = function(data) {
  let result = { data: [], ohlc: {}, loading: false };
  result["data"] = data;
  let ohlc = JSON.parse(JSON.stringify(data[data.length - 1]));
  result["ohlc"] = {
    open: +ohlc.open,
    high: +ohlc.high,
    low: +ohlc.low,
    close: +ohlc.close
  };
  return result;
};

/**
 * Gets all years available in nested data and idx of those years
 */
Analytics.prototype.getYears = function() {
  if (this.rawData["nestedData"].length === 0) {
    return;
  }
  let _this = this,
    yrs = [],
    yr,
    sw = false;
  this.rawData["years"] = {};
  this.rawData["nestedData"].forEach(function(d, i) {
    yr = d.date.getFullYear();
    if (!yrs.includes(yr)) {
      yrs.push(yr);
      _this.rawData["years"][yr] = { initial: i };
      if (sw) {
        _this.rawData["years"][yr - 1]["final"] = i;
      }
      sw = true;
    }
  });
  this.rawData["years"][yr]["final"] = this.rawData["nestedData"].length;
  this.rawData["years"]["range"] = [yrs[0], yrs[yrs.length - 1]];
  let year_selected = new Date().getFullYear();
  if (yrs.includes(year_selected)) {
    Object.assign(_this.userSelections, { year: year_selected });
  } else {
    Object.assign(_this.userSelections, { year: Math.max(...yrs) });
  }
  let xRange = [
    this.rawData["years"][this.userSelections["year"]]["initial"],
    this.rawData["years"][this.userSelections["year"]]["final"]
  ];
  Object.assign(_this.userSelections, { xZoom: xRange });
};

/**
 * Retrieves user rawData for selected company/portfolio/asset
 */
Analytics.prototype.getAllRawData = function(initial = true) {
  // loadingExpoHedges when finished
  Object.assign(this.loading, {
    budget: true,
    expoFuture: true,
    expoPast: true,
    hedgesFuture: true,
    hedgesPast: true,
    pastCalcs: true,
    portCalcs: true,
    program: true,
    strategies: true,
    derivatives: true,
    topographyCalcs: true,
    topographyCalcsRates: true
  });
  let initialDate = initial
    ? new Date(new Date().setFullYear(new Date().getFullYear() - 5))
    : new Date(new Date().setFullYear(new Date().getFullYear() - 11));
  initialDate = timeDay(timeDay(initialDate.setMonth(0)).setDate(1));
  this.resetData();
  this.getCmdtyRawExpoFuture();
  this.getCmdtyRawExpoPast(formatDate_YMD(initialDate));
  this.getCmdtyRawHedgeFuture();
  this.getCmdtyRawHedgePast(formatDate_YMD(initialDate));
  // this.getCmdtyRawProgram();
  Object.assign(this.rawData, { toBeFetched: false });
  Object.assign(this.rawData, { toBeFetchedHidden: true });
};

/**
 * Retrieves user Cmdty Expo Future rawData for selected company/portfolio/asset
 */
Analytics.prototype.getCmdtyRawExpoFuture = function() {
  let _this = this;
  Object.assign(this.rawData, {
    expoFuture: []
  });
  let params = {
    method: "GET",
    endpoint: "commodity_future_exposure",
    content_type: "application/json",
    query_params: {
      format: "json",
      end_product: { id_assets: this.userSelections["assetsNums"] },
      portfolios: { id_portfolios: this.userSelections["portfolioNums"] },
      date: formatDate_YMD(new Date())
    }
  };
  apiService
    .getAll(params)
    .then(data => {
      Object.assign(this.rawData, {
        expoFuture: this.processRawExpoCmdty(data)
      });
      Object.assign(this.loading, { expoFuture: false });
    })
    .catch(function(error) {
      Object.assign(_this.loading, { expoFuture: false });
      console.log(error);
    });
};

/**
 * Retrieves user Cmdty Expo Past rawData for selected company/portfolio/asset
 */
Analytics.prototype.getCmdtyRawExpoPast = function(initialDate) {
  let _this = this;
  Object.assign(this.rawData, {
    expoPast: []
  });
  let params = {
    method: "GET",
    endpoint: "commodity_past_exposure",
    content_type: "application/json",
    query_params: {
      format: "json",
      end_product: { id_assets: this.userSelections["assetsNums"] },
      portfolios: { id_portfolios: this.userSelections["portfolioNums"] },
      date: formatDate_YMD(new Date()),
      initialDate: initialDate
    }
  };
  apiService
    .getAll(params)
    .then(data => {
      Object.assign(this.rawData, {
        expoPast: this.processRawExpoCmdty(data)
      });
      Object.assign(this.loading, { expoPast: false });
    })
    .catch(function(error) {
      Object.assign(_this.loading, { expoPast: false });
      console.log(error);
    });
};

/**
 * Procces Raw Cmdty Expo data for selected company/portfolio/asset
 */
Analytics.prototype.processRawExpoCmdty = function(data) {
  data = JSON.parse(JSON.stringify(data));
  data.forEach(function(d) {
    d.date = timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(d.date);
    d.p_date = timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(d.p_date);
    d.sale_date = timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(d.sale_date);
    d.futures = d.commodity;
  });
  return data;
};

/**
 * Retrieves user Cmdty Hedges Future rawData for selected company/portfolio/asset
 */
Analytics.prototype.getCmdtyRawHedgeFuture = function() {
  let _this = this;
  Object.assign(this.rawData, {
    hedgesFuture: []
  });
  let params = {
    method: "GET",
    endpoint: "commodity_future_hedge",
    content_type: "application/json",
    query_params: {
      format: "json",
      end_product: { id_assets: this.userSelections["assetsNums"] },
      portfolios: { id_portfolios: this.userSelections["portfolioNums"] },
      date: formatDate_YMD(new Date())
    }
  };
  apiService
    .getAll(params)
    .then(data => {
      Object.assign(this.rawData, {
        hedgesFuture: this.processRawHedgeCmdty(data)
      });
      Object.assign(this.loading, { hedgesFuture: false });
    })
    .catch(function(error) {
      Object.assign(_this.loading, { hedgesFuture: false });
      console.log(error);
    });
};

/**
 * Retrieves user Cmdty Hedges Past rawData for selected company/portfolio/asset
 */
Analytics.prototype.getCmdtyRawHedgePast = function(initialDate) {
  let _this = this;
  Object.assign(this.rawData, {
    hedgesPast: []
  });
  let params = {
    method: "GET",
    endpoint: "commodity_past_hedge",
    content_type: "application/json",
    query_params: {
      format: "json",
      end_product: { id_assets: this.userSelections["assetsNums"] },
      portfolios: { id_portfolios: this.userSelections["portfolioNums"] },
      date: formatDate_YMD(new Date()),
      initialDate: initialDate
    }
  };
  apiService
    .getAll(params)
    .then(data => {
      Object.assign(this.rawData, {
        hedgesPast: this.processRawHedgeCmdty(data)
      });
      Object.assign(this.loading, { hedgesPast: false });
    })
    .catch(function(error) {
      Object.assign(_this.loading, { hedgesPast: false });
      console.log(error);
    });
};

/**
 * Procces Raw Cmdty Hedge data for selected company/portfolio/asset
 */
Analytics.prototype.processRawHedgeCmdty = function(data) {
  let condition;
  data = JSON.parse(JSON.stringify(data));
  data.forEach(function(d) {
    d.date = timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(d.fixing_date);
    d.premium_value_date = timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(
      d.premium_value_date
    );
    d.trade_date = timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(d.trade_date);
    d.value_date = timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(d.value_date);
    d.hedged_date = timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(d.hedged_date);
    d.sale_date = timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(d.sale_date);
    d.notional = +d.quantity;
    d.derivative_name = `${longShort(d.buy_sell)} ${d.derivative}`;
    // d.strike = +d.strike * d.price_change;
    condition = hedgeConditionNew(d.derivative_purpose);
    if (d.derivative_type.indexOf("OUT") > -1 && d.new_event) {
      d.hedgeNotional = 0;
    } else {
      if (d.derivative_type.indexOf("IN") > -1 && !d.new_event) {
        d.hedgeNotional = 0;
      } else {
        d.hedgeNotional = condition ? +d.notional * d.undelying_position : 0;
      }
    } // TODO: puede venir del back
    d.cash_flow = 0;
    d.balance = 0;
  });
  return data;
};

/**
 * Process Raw Cmdty Program data for selected company/portfolio/asset
 */
Analytics.prototype.getCmdtyRawProgram = function() {
  let _this = this;
  this["rawData"]["program"] = [];
  let params = {
    method: "GET",
    endpoint: "commodity_program",
    content_type: "application/json",
    query_params: {
      format: "json",
      commodity: parseInt(
        this.userSelections["assets"][this.userSelections["selectedAsset"]][
          "pk_id_end_product"
        ]
      ),
      portfolios: { id_portfolios: this.userSelections["portfolioNums"] }
    }
  };
  apiService
    .getAll(params)
    .then(data => {
      data.forEach(function(d) {
        d.flow = JSON.parse(d.flow);
      });
      this["rawData"]["program"] = data;
      this["loading"]["program"] = false;
    })
    .catch(function(error) {
      _this["loading"]["program"] = false;
      console.log(error);
    });
};

/**
 * Consolidate expo and hedges
 */
Analytics.prototype.consolidateExpoHedges = function() {
  let _this = this;
  if (
    this["rawData"]["expoFuture"].length === 0 &&
    this["rawData"]["expoPast"].length === 0 &&
    this["rawData"]["hedgesFuture"].length === 0 &&
    this["rawData"]["hedgesPast"].length === 0
  ) {
    Object.assign(this.loading, { topographyCalcs: false });
    return;
  }
  Object.assign(this.loading, {
    topographyCalcs: true
  });
  let params = {
    method: "POST",
    endpoint: "expo_hedges_consolidation",
    content_type: "application/json",
    query_params: {
      companies: {
        id_companies: this.userSelections["selectedCompanies"].map(
          d => d.pk_idcompany
        )
      },
      format: "json",
      expoFuture: JSON.stringify(this["rawData"]["expoFuture"]),
      expoPast: JSON.stringify(this["rawData"]["expoPast"]),
      hedgesFuture: JSON.stringify(this["rawData"]["hedgesFuture"]),
      hedgesPast: JSON.stringify(this["rawData"]["hedgesPast"])
    }
  };
  return apiService
    .getAll(params)
    .then(data => {
      data = JSON.parse(JSON.stringify(data));
      if (Object.keys(data).length === 0) {
        this.resetData();
      } else {
        data.forEach(function(d) {
          d.date = timeParseNoNull2(d.date);
          d.purchase_date = timeParseNoNull(d.purchase_date);
          d.fixation_date = timeParseNoNull2(d.fixation_date);
          d.fixing_date = timeParseNoNull2(d.fixing_date);
          d.p_date = timeParseNoNull(d.p_date);
          d.sale_date = timeParseNoNull(d.sale_date);
          d.premium_value_date = timeParseNoNull(d.premium_value_date);
          d.trade_date = timeParseNoNull2(d.trade_date);
          d.value_date = timeParseNoNull2(d.value_date);
          d.hedged_date = timeParseNoNull(d.hedged_date);
          d.notional = +d.quantity;
          _this.existDerivatives(d);
          // d.buy_sell = d.buy_sell != null ? d.buy_sell.toUpperCase() : null;
          // d.derivative = d.derivative != null ? d.derivative.toUpperCase() : null;
          // if (d.derivative_type.indexOf("OUT") > -1 && d.new_event) {
          //   d.hedgeNotional = 0;
          // } else {
          //   if (d.derivative_type.indexOf("IN") > -1 && !d.new_event) {
          //     d.hedgeNotional = 0;
          //   } else {
          //     d.hedgeNotional = +d.notional * d.undelying_position;
          //   }
          // }
          // d.cash_flow = 0;
          // d.balance = 0;
        });
        Object.assign(this.rawData, {
          zoomedData: [],
          mergedExpoHedges: data,
          dDowns: this.createDDowns([{ details: data }]),
          dataFilters: defineFilters(),
          filteredData: data
        });
        this.nestAndZoom(data);
      }
    })
    .catch(function(error) {
      Object.assign(_this.loading, { topographyCalcs: false });
      console.log(error);
    });
};

/**
 * Function to determine what type of derivatives exist
 * @param {object} d: data entry
 */
Analytics.prototype.existDerivatives = function(d) {
  if (d.buy_sell === "long") {
    if (d.derivative === "forward" || d.derivative === "future") {
      Object.assign(this.rawData, { existLFwd: true });
    } else if (d.derivative === "call") {
      Object.assign(this.rawData, { existLCall: true });
    } else if (d.derivative === "put") {
      Object.assign(this.rawData, { existLPut: true });
    }
  } else if (d.buy_sell === "short") {
    if (d.derivative === "forward" || d.derivative === "future") {
      Object.assign(this.rawData, { existSFwd: true });
    } else if (d.derivative === "call") {
      Object.assign(this.rawData, { existSCall: true });
    } else if (d.derivative === "put") {
      Object.assign(this.rawData, { existSPut: true });
    }
  }
};

/**
 * Nest
 * @param {array} data: data to filter
 */
Analytics.prototype.nestAll = function(data) {
  let _this = this;
  let result = nestCommoditiesFct(data, _this.userSelections["intervalGroup"]);
  Object.assign(_this.rawData, { toBeFetched: false });
  return result;
};

/**
 * Nest and zoom after filtering
 */
Analytics.prototype.nestAndZoom = function(data) {
  Object.assign(this.rawData, {
    nestedData: this.nestAll(data)
  });
  this.getYears();
  Object.assign(this.rawData, {
    zoomedData: this.zoomData(
      this.rawData["nestedData"],
      this.userSelections["xZoom"]
    )
  });
  this.filterHedges();
  Object.assign(this.rawData, {
    totals: [zoomedTotals(this.rawData["zoomedData"])]
  });
  Object.assign(this.loading, { topographyCalcs: false });
  let bool = !this.rawData["render"];
  Object.assign(this.rawData, { render: bool });
};

/**
 * filterHedges for derivatives chart filtering
 */
Analytics.prototype.filterHedges = function() {
  let len = this.rawData["zoomedData"].length - 1;
  if (len < 0) {
    return;
  }
  let lastDate = this.rawData["zoomedData"][len].date;
  let xMax = timeDay(
    timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(JSON.parse(JSON.stringify(lastDate)))
  );
  let intGr = this.userSelections["intervalGroup"];
  if (intGr === "1D") {
    xMax.setDate(xMax.getDate() + 1);
  } else if (intGr === "1W") {
    xMax.setDate(xMax.getDate() + 7);
  } else if (intGr === "2W") {
    xMax.setDate(xMax.getDate() + 15);
  } else if (intGr === "1M") {
    xMax.setMonth(xMax.getMonth() + 1);
  } else if (intGr === "1Q") {
    xMax.setMonth(xMax.getMonth() + 3);
  } else if (intGr === "6M") {
    xMax.setMonth(xMax.getMonth() + 6);
  } else if (intGr === "1Y") {
    xMax.setFullYear(xMax.getFullYear() + 1);
  }
  let minDate = +this.rawData["zoomedData"][0].date;
  let maxDate = +xMax;
  let hedgeData = this.rawData["hedgesPast"].concat(
    this.rawData["hedgesFuture"]
  );
  let expoData = this.rawData["expoPast"].concat(this.rawData["expoFuture"]);
  Object.assign(this.rawData, {
    zoomedDerivatives: hedgeData.filter(
      d => +d.date >= minDate && +d.date <= maxDate
    ),
    filteredExpoData: expoData.filter(
      d => +d.date >= minDate && +d.date < maxDate
    )
  });
  // === Data derivatives
  getDataNestAnalysisScenarios({
    _this: this,
    intervalGroup: intGr,
    nameData: "derivatives",
    analysisFieldName: "derivative_name",
    valueQty: "notional",
    calculateDelta: true,
    filterData: "zoomedDerivatives"
  });
  // === Data strategies
  getDataNestAnalysisScenarios({
    _this: this,
    intervalGroup: intGr,
    nameData: "strategies",
    analysisFieldName: "strategy_name",
    valueQty: "hedge_value",
    calculateStrike: false,
    filterData: "zoomedDerivatives"
  });
  // === Data providers
  getDataNestAnalysisScenarios({
    _this: this,
    intervalGroup: intGr,
    nameData: "providers",
    analysisFieldName: "fk_id_provider",
    valueQty: "quantity",
    calculateStrike: false,
    filterData: "filteredExpoData",
    ignore_field: "exposure_type",
    ignore_values: ["BUDGET", "PROJECTED"]
  });
  // === Data clients
  getDataNestAnalysisScenarios({
    _this: this,
    intervalGroup: intGr,
    nameData: "clients",
    analysisFieldName: "fk_id_client",
    valueQty: "quantity",
    calculateStrike: false,
    filterData: "filteredExpoData"
  });
  // === Data counterparty
  getDataNestAnalysisScenarios({
    _this: this,
    intervalGroup: intGr,
    nameData: "counterparty",
    analysisFieldName: "counterparty",
    valueQty: "quantity",
    calculateStrike: false,
    filterData: "filteredExpoData"
  });
};

/**
 * getSpot
 */
Analytics.prototype.getSpot = function() {
  let _this = this;
  Object.assign(this.loading, {
    topographyCalcs: true
  });
  let params = {
    method: "POST",
    endpoint: "expo_hedges_consolidation",
    content_type: "application/json",
    query_params: {
      format: "json",
      expoFuture: JSON.stringify(this["rawData"]["expoFuture"]),
      expoPast: JSON.stringify(this["rawData"]["expoPast"]),
      hedgesFuture: JSON.stringify(this["rawData"]["hedgesFuture"]),
      hedgesPast: JSON.stringify(this["rawData"]["hedgesPast"])
    }
  };
  return apiService
    .getAll(params)
    .then(data => {
      data = JSON.parse(JSON.stringify(data));
      data.forEach(function(d) {
        d.date = timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(d.date);
        d.purchase_date = timeParse("%Y-%m-%d")(d.purchase_date);
        d.fixation_date = timeParse("%Y-%m-%d")(d.fixation_date);
        d.p_date = timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(d.p_date);
        d.sale_date = timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(d.sale_date);
        d.premium_value_date = timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(
          d.premium_value_date
        );
        d.trade_date = timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(d.trade_date);
        d.value_date = timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(d.value_date);
        d.hedged_date = timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(d.hedged_date);
        d.notional = +d.quantity;
        // if (d.derivative_type.indexOf("OUT") > -1 && d.new_event) {
        //   d.hedgeNotional = 0;
        // } else {
        //   if (d.derivative_type.indexOf("IN") > -1 && !d.new_event) {
        //     d.hedgeNotional = 0;
        //   } else {
        //     d.hedgeNotional = +d.notional * d.undelying_position;
        //   }
        // }
        // d.cash_flow = 0;
        // d.balance = 0;
      });
      Object.assign(this.rawData, { mergedExpoHedges: data });
      Object.assign(this.loading, { topographyCalcs: false });
    })
    .catch(function(error) {
      Object.assign(_this.loading, { topographyCalcs: false });
      console.log(error);
    });
};

/**
 * Consolidate expo and hedges
 */
Analytics.prototype.getPortfolioValuation = function() {
  let _this = this;
  Object.assign(this.valuation, { valuated: true });
  Object.assign(this.valuation, { data: [] });
  Object.assign(this.loading, { accounting: true });
  let endpoint_string =
    this.assetType === "commodity"
      ? "commodity_portfolio_valuation"
      : "currency_portfolio_valuation";
  let params = {
    method: "POST",
    endpoint: endpoint_string,
    content_type: "application/json",
    query_params: {
      assets: { id_assets: this.userSelections["underlyingNums"] },
      endDate: formatDate_YMD(this.valuation["finalDate"]),
      format: "json",
      initialDate: formatDate_YMD(this.valuation["initialDate"]),
      portfolios: { id_portfolios: this.userSelections["portfolioNums"] }
    }
  };
  return apiService
    .getAll(params)
    .then(data => {
      Object.assign(this.valuation, { data: data });
      this.valuation["data"].forEach(function(dat) {
        dat.selected = [];
        dat.details.forEach(function(d) {
          d.date = timeParseNoNull2(d.fixing_date);
          d.premium_value_date = timeParseNoNull2(d.premium_value_date);
          d.trade_date = timeParseNoNull2(d.trade_date);
          d.value_date = timeParseNoNull2(d.value_date);
          d.hedged_date = timeParseNoNull2(d.hedged_date);
          d.sale_date = timeParseNoNull2(d.sale_date);
          d.futures = _this.underlyingObject[d.commodity].commodity_name;
          d.notional = +d.quantity;
          if (d.derivative_type.indexOf("OUT") > -1 && d.new_event) {
            d.hedgeNotional = 0;
          } else {
            if (d.derivative_type.indexOf("IN") > -1 && !d.new_event) {
              d.hedgeNotional = 0;
            } else {
              d.hedgeNotional = +d.notional * d.undelying_position;
            }
          } // TODO: puede venir del back
          d.buy_sell = longShortTransform(d.buy_sell.toUpperCase());
          d.derivative = d.derivative.toUpperCase();
          d.cash_flow = 0;
          d.balance = 0;

          d.asian_fixing_start_date = timeParseNoNull2(
            d.asian_fixing_start_date
          );
          d.dImg = derivativeImage(
            {
              derivative: d.derivative.toLowerCase(),
              buy_sell: d.buy_sell.toLowerCase()
            },
            _this.images["lFwd"],
            _this.images["sFwd"],
            _this.images["lCall"],
            _this.images["sCall"],
            _this.images["lPut"],
            _this.images["sPut"]
          );
          d.strategy_name = d.strategy_name.toUpperCase();
        });
      });
      Object.assign(this.valuation, {
        dDowns: this.createDDowns(this.valuation["data"])
      });
      Object.assign(this.loading, { accounting: false });
    })
    .catch(function(error) {
      Object.assign(_this.loading, { accounting: false });
      console.log(error);
    });
};

/**
 * Create dropdown for a given field for valuation
 * @param {array} allData: data to filter
 * @param {string} fieldName: field to filter
 * @param {boolean} futSort: field if data is futures contract date MMMYY
 */
Analytics.prototype.valuationDDowns = function(
  allData,
  fieldName,
  futSort = false
) {
  let dDown = [];
  allData.forEach(function(data) {
    dDown = dDown.concat(data.details.map(d => d[fieldName]));
  });
  if (futSort) {
    dDown = dDown
      .sort((a, b) => +timeParse("%m%Y")(a) - +timeParse("%m%Y")(b))
      .filter(this.processData.onlyUnique)
      .filter(d => d != null && d !== "");
  } else {
    dDown = dDown
      .sort()
      .filter(this.processData.onlyUnique)
      .filter(d => d != null && d !== "");
  }
  return dDown;
};

/**
 * Create dropdowns for Valuation Filters
 * @param {array} dDownData: data to filter
 */
Analytics.prototype.createDDowns = function(dDownData) {
  let dDowns = {};
  dDowns["businessDDown"] = this.valuationDDowns(dDownData, "fk_id_business");
  dDowns["clientDDown"] = this.valuationDDowns(dDownData, "fk_id_client");
  dDowns["commoditiesDDown"] = this.valuationDDowns(
    dDownData,
    "end_product_str"
  );
  dDowns["contractDDown"] = this.valuationDDowns(
    dDownData,
    "contract_month_year",
    true
  );
  dDowns["counterPDDown"] = this.valuationDDowns(dDownData, "counterparty");
  dDowns["countryDDown"] = this.valuationDDowns(
    dDownData,
    "country_of_destination"
  );
  dDowns["expiredDDown"] = ["Active", "Expired"];
  dDowns["expoCodeDDown"] = this.valuationDDowns(dDownData, "custom_id");
  dDowns["expoTypeDDown"] = this.valuationDDowns(dDownData, "exposure_type");
  dDowns["fixStatusDDown"] = ["Fixed", "Not Fixed"];
  dDowns["futuresDDown"] = this.valuationDDowns(dDownData, "commodity_hedge");
  dDowns["originDDown"] = this.valuationDDowns(
    dDownData,
    "fk_id_country_origin"
  );
  dDowns["plantDDown"] = this.valuationDDowns(dDownData, "fk_id_plant");
  dDowns["portfoliosDDown"] = this.valuationDDowns(dDownData, "portfolio");
  dDowns["pricingMethodDDown"] = this.valuationDDowns(
    dDownData,
    "pricing_method"
  );
  dDowns["strategiesDDown"] = this.valuationDDowns(dDownData, "strategy_name");
  dDowns["supplierDDown"] = this.valuationDDowns(dDownData, "fk_id_provider");
  return dDowns;
};

/**
 * zoom data
 */
Analytics.prototype.zoomData = function(data, zoomVals) {
  return data.slice(zoomVals[0], zoomVals[1]);
};

/**
 * Reset Data
 */
Analytics.prototype.resetData = function() {
  Object.assign(this.rawData, {
    budget: [],
    dataFilters: defineFilters(),
    dDowns: defineDDowns(),
    expoFuture: [],
    expoPast: [],
    filteredData: [],
    hedgesFuture: [],
    hedgesPast: [],
    historicalData: [],
    historicalOHLC: {},
    mergedExpoHedges: [],
    nestedData: [],
    program: [],
    totals: defineTotals(),
    years: {},
    zoomedData: []
  });
};

/**
 * Define filters for all Analytics
 */
export function defineFilters() {
  return {
    base: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }]
    },
    bonus: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }]
    },
    buy_sell: { value: null, matchMode: FilterMatchMode.IN },
    client: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
    },
    comments: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
    },
    commodity_unit: { value: null, matchMode: FilterMatchMode.IN },
    commodity: { value: null, matchMode: FilterMatchMode.IN },
    continuation_month: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }]
    },
    contract_month_year: { value: null, matchMode: FilterMatchMode.IN },
    counterparty: { value: null, matchMode: FilterMatchMode.IN },
    country_of_destination: { value: null, matchMode: FilterMatchMode.IN },
    custom_id: { value: null, matchMode: FilterMatchMode.IN },
    custom_name: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
    },
    date: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }]
    },
    derivative: { value: null, matchMode: FilterMatchMode.IN },
    end_product: { value: null, matchMode: FilterMatchMode.IN },
    eta: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }]
    },
    expired: { value: null, matchMode: FilterMatchMode.IN },
    exposure_type: { value: null, matchMode: FilterMatchMode.IN },
    fixation_date: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }]
    },
    fixation_status: { value: null, matchMode: FilterMatchMode.IN },
    fk_id_business: { value: null, matchMode: FilterMatchMode.IN },
    fk_id_client: { value: null, matchMode: FilterMatchMode.IN },
    fk_id_country_origin: { value: null, matchMode: FilterMatchMode.IN },
    fk_id_end_product: { value: null, matchMode: FilterMatchMode.IN },
    fk_id_plant: { value: null, matchMode: FilterMatchMode.IN },
    fk_id_provider: { value: null, matchMode: FilterMatchMode.IN },
    fk_idincoterms: { value: null, matchMode: FilterMatchMode.IN },
    freight: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }]
    },
    futures: { value: null, matchMode: FilterMatchMode.IN },
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    hedge_asset: { value: null, matchMode: FilterMatchMode.IN },
    insurance: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }]
    },
    percent_bonus: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }]
    },
    plant: { value: null, matchMode: FilterMatchMode.IN },
    portfolio: { value: null, matchMode: FilterMatchMode.IN },
    pricing_method: { value: null, matchMode: FilterMatchMode.IN },
    provider: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
    },
    purchase_date: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }]
    },
    quantity: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }]
    },
    quotation: { value: null, matchMode: FilterMatchMode.IN },
    ratio: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }]
    },
    sale_date: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }]
    },
    settlement_pnl: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }]
    },
    settlement_price: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }]
    },
    shipping_date: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }]
    },
    strike: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }]
    },
    tariffs: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }]
    }
  };
}

/**
 * Define dropdowns for all Analytics
 */
export function defineDDowns() {
  return {
    businessDDown: [],
    clientDDown: [],
    commoditiesDDown: [],
    contractDDown: [],
    counterPDDown: [],
    countryDDown: [],
    expiredDDown: [],
    expoCodeDDown: [],
    expoTypeDDown: [],
    fixStatusDDown: [],
    futuresDDown: [],
    originDDown: [],
    plantDDown: [],
    portfoliosDDown: [],
    pricingMethodDDown: [],
    strategiesDDown: [],
    supplierDDown: []
  };
}

/**
 * Define empty valuation table for all Analytics
 */
export function defineEmptyValTable() {
  return [
    {
      totals: [
        {
          txt_quantity: "Quantity",
          quantity: 0,
          txt_delta: "Delta",
          delta: 0,
          txt_derivatives: "Derivatives",
          n_derivatives: 0,
          txt_strats: "Strategies",
          n_strats: 0,
          txt_value: "Valuation on MM DD, YYY",
          hedge_value: 0,
          txt_value_initial: "Valuation on MM DD, YYY",
          hedge_value_initial: 0
        }
      ],
      details: [],
      show: false,
      name: "Expired hedges",
      selected: []
    },
    {
      totals: [
        {
          txt_quantity: "Quantity",
          quantity: 0,
          txt_delta: "Delta",
          delta: 0,
          txt_derivatives: "Derivatives",
          n_derivatives: 0,
          txt_strats: "Strategies",
          n_strats: 0,
          txt_value: "Valuation on MM DD, YYY",
          hedge_value: 0,
          txt_value_initial: "Valuation on MM DD, YYY",
          hedge_value_initial: 0
        }
      ],
      details: [],
      show: false,
      name: "NEW HEDGES",
      selected: []
    },
    {
      totals: [
        {
          txt_quantity: "Quantity",
          quantity: 0,
          txt_delta: "Delta",
          delta: 0,
          txt_derivatives: "Derivatives",
          n_derivatives: 0,
          txt_strats: "Strategies",
          n_strats: 0,
          txt_value: "Valuation on MM DD, YYY",
          hedge_value: 0,
          txt_value_initial: "Valuation on MM DD, YYY",
          hedge_value_initial: 0
        }
      ],
      details: [],
      show: false,
      name: "OTHER HEDGES",
      selected: []
    },
    {
      totals: [
        {
          txt_quantity: "Quantity",
          quantity: 0,
          txt_delta: "Delta",
          delta: 0,
          txt_derivatives: "Derivatives",
          n_derivatives: 0,
          txt_strats: "Strategies",
          n_strats: 0,
          txt_value: "Valuation on MM DD, YYY",
          hedge_value: 0,
          txt_value_initial: "Valuation on MM DD, YYY",
          hedge_value_initial: 0
        }
      ],
      details: [],
      show: false,
      name: "TOTAL OPEN POSITIONS",
      selected: []
    }
  ];
}

/**
 * Define empty totals for all Analytics
 */
export function defineTotals() {
  return {
    date: "...",
    budgetQ: 0,
    forecastQ: 0,
    ratioQ: 0,
    fixedQ: 0,
    hedgedQ: 0,
    budgetQNorm: 0,
    forecastQNorm: 0,
    ratioQNorm: 0,
    fixedQNorm: 0,
    hedgedQNorm: 0,
    ratioQPerc: 0,
    fixedQPerc: 0,
    hedgedQPerc: 0,
    budgetP: 0,
    fixedP: 0,
    marketP: 0,
    netP: 0,
    fixedPPerc: 0,
    marketPPerc: 0,
    netPPerc: 0,
    budgetPOther: 0,
    fixedPOther: 0,
    marketPOther: 0,
    netPOther: 0,
    fixedPOtherPerc: 0,
    marketPOtherPerc: 0,
    netPOtherPerc: 0,
    budgetPRatio: 0,
    fixedPRatio: 0,
    marketPRatio: 0,
    netPRatio: 0,
    fixedPRatioPerc: 0,
    marketPRatioPerc: 0,
    netPRatioPerc: 0,
    budgetPFut: 0,
    fixedPFut: 0,
    marketPFut: 0,
    netPFut: 0,
    fixedPFutPerc: 0,
    marketPFutPerc: 0,
    netPFutPerc: 0,
    budgetPFutRatio: 0,
    fixedPFutRatio: 0,
    marketPFutRatio: 0,
    netPFutRatio: 0,
    fixedPFutRatioPerc: 0,
    marketPFutRatioPerc: 0,
    netPFutRatioPerc: 0
  };
}

/**
 * Define empty Analytics Variable
 */
export function defaultAnalytics() {
  return {
    companies: [{ id: 0, company_name: "N.A." }],
    loading: {
      expoFuture: false
    },
    rawData: {
      budget: [],
      dataFilters: defineFilters(),
      dDowns: defineDDowns(),
      derivatives: [],
      expoFuture: [],
      expoPast: [],
      hedgesFuture: [],
      hedgesPast: [],
      historicalData: [],
      historicalOHLC: {},
      nestedData: [],
      program: [],
      strategies: [],
      toBeFetched: true,
      toBeFetchedHidden: false,
      years: [],
      zoomedData: []
    },
    underlyingSpecs: [],
    userSelections: {
      assets: [
        {
          product_name: "N.A.",
          fk_idcurrency: "N.A.",
          fk_idunit: "N.A.",
          currency_name: "N.A/N.A"
        }
      ],
      bucket: "MONTHLY",
      expandSidebar: false,
      initStoreCompPortAsset: null,
      portfolioNums: [],
      portfolios: [],
      selectedAnalysis: "scenarios",
      selectedAnalysisParent: "scenario",
      selectedAsset: [
        {
          product_name: "N.A.",
          fk_idcurrency: "N.A.",
          fk_idunit: "N.A.",
          currency_name: "N.A/N.A"
        }
      ],
      selectedCompanies: [{ id: 0, company_name: "N.A." }],
      selectedPortfolios: [],
      selectedTable: "strategies",
      selectedUnderlying: [{ commodity_name: "N.A." }],
      underlying: [{ commodity_name: "N.A." }],
      thenCandleData: null,
      visibleLeft: false,
      xZoom: []
    },
    valuation: {
      data: defineEmptyValTable(),
      dDowns: defineDDowns(),
      finalDate: null,
      finalSpot: null,
      initialDate: null,
      initialSpot: null,
      selectedAsset: [],
      valuated: false
    }
  };
}

/**
 * Retrieves String to show on title for Analytics Commodity
 * @param {object} data: analytics commodity class
 */
export function stringCommodityTitle(data) {
  let txt = selectionStrFct(
    data["userSelections"]["selectedCompanies"],
    "company_name",
    " companies"
  );
  if (data["rawData"]["dataFilters"] !== undefined) {
    // == portfolios
    if (data["rawData"]["dataFilters"]["portfolio"]["value"] != null) {
      txt +=
        " | " +
        selectionStrFct(
          data["rawData"]["dataFilters"]["portfolio"]["value"],
          null,
          " portfolios"
        );
    } else {
      if (data["userSelections"]["selectedPortfolios"] != null) {
        txt +=
          " | " +
          selectionStrFct(
            data["userSelections"]["selectedPortfolios"],
            "name",
            " portfolios"
          );
      }
    }
    // == products
    if (data["assetType"] === "commodity") {
      if (
        data["rawData"]["dataFilters"]["fk_id_end_product"]["value"] != null
      ) {
        txt +=
          " | " +
          selectionStrFct(
            data["rawData"]["dataFilters"]["fk_id_end_product"]["value"],
            null,
            " products"
          );
      } else {
        if (data["userSelections"]["selectedAsset"] != null) {
          txt +=
            " | " +
            selectionStrFct(
              data["userSelections"]["selectedAsset"],
              "product_name",
              " products"
            );
        }
      }
    }
    // == country
    if (
      data["rawData"]["dataFilters"]["country_of_destination"]["value"] != null
    ) {
      txt +=
        " | " +
        selectionStrFct(
          data["rawData"]["dataFilters"]["country_of_destination"]["value"],
          null,
          " countries"
        );
    } else {
      if (data["rawData"]["dDowns"]["countryDDown"].length > 0) {
        txt +=
          " | " +
          selectionStrFct(
            data["rawData"]["dDowns"]["countryDDown"],
            null,
            " countries"
          );
      }
    }
    // == business
    if (data["rawData"]["dataFilters"]["fk_id_business"]["value"] != null) {
      txt +=
        " | " +
        selectionStrFct(
          data["rawData"]["dataFilters"]["fk_id_business"]["value"],
          null,
          " business"
        );
    } else {
      if (data["rawData"]["dDowns"]["businessDDown"].length > 0) {
        txt +=
          " | " +
          selectionStrFct(
            data["rawData"]["dDowns"]["businessDDown"],
            null,
            " business"
          );
      }
    }
  }
  return txt;
}
/**
 * Retrieves String to show on title for Analytics Commodity
 * @param {object} selVar: selected portfolio, country, or etc.
 * @param {string} strField: field name
 * @param {string} defaultField: default field name
 */
export function selectionStrFct(selVar, strField, defaultField) {
  let txt = "";
  if (selVar.length === 0) {
    txt = "";
  } else if (selVar.length > 1) {
    txt = selVar.length + defaultField;
  } else {
    selVar.forEach(function(d) {
      txt += strField == null ? d : d[strField];
    });
  }
  return txt;
}

export function nestingMainCalcs(v, intervalGroup, data, sign) {
  let date = timeComparison(values(v).pop().date, intervalGroup, data);
  // QUANTITIES ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ====
  let forecastQ = sum(v, function(d) {
    return d.cash_flow;
  });
  let budgetQ =
    sign *
    sum(v, function(d) {
      return d.budget_expo;
    });
  forecastQ = sign * forecastQ;
  let basisQ =
    sign *
    sum(v, function(d) {
      return d.balance;
    });
  let fixedQ =
    sign *
    sum(v, function(d) {
      return isFixationDerivative(d.derivative)
        ? d.hedgeNotional / d.ratio / d.unit_change
        : 0;
    });
  let fixedHedgedFutures =
    sign *
    sum(v, function(d) {
      return d.hedgeNotional;
    });
  let fixedHedged =
    sign *
    sum(v, function(d) {
      return d.hedgeNotional / d.ratio / d.unit_change;
    });
  let hedgedQ = fixedHedged - fixedQ;
  let hedgeValue = sum(v, function(d) {
    return d.hedge_value;
  });

  // BUDGET ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ====
  let budgetBasis =
    (sign *
      sum(v, function(d) {
        return d.cash_flow !== 0 ? d.base * d.cash_flow : 0;
      })) /
    forecastQ;
  let budgetPRatio =
    (sign *
      sum(v, function(d) {
        return d.cash_flow !== 0 ? d.ratio * d.cash_flow : 0;
      })) /
    forecastQ;
  let budgetPFut =
    (sign *
      sum(v, function(d) {
        return d.cash_flow !== 0 ? d.budget_futures * d.cash_flow : 0;
      })) /
    forecastQ;
  let budgetP =
    (sign *
      sum(v, function(d) {
        return d.cash_flow !== 0 ? d.budget_price * d.cash_flow : 0;
      })) /
    forecastQ;

  // MARKET ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ====
  let marketBasis, marketPRatio, marketPFut, marketP;
  if (forecastQ === 0) {
    marketBasis = NaN;
    marketPRatio = NaN;
    marketPFut = NaN;
    marketP = NaN;
  } else {
    marketBasis =
      (sign *
        sum(v, function(d) {
          return d.cash_flow !== 0 ? d.mkt_basis * d.cash_flow : 0;
        })) /
      forecastQ;
    marketPRatio =
      (sign *
        sum(v, function(d) {
          return d.cash_flow !== 0 ? d.mkt_ratio * d.cash_flow : 0;
        })) /
      forecastQ;
    marketPFut =
      (sign *
        sum(v, function(d) {
          return d.cash_flow !== 0 ? d.fwd * d.cash_flow : 0;
        })) /
      forecastQ;
    marketP =
      (sign *
        sum(v, function(d) {
          return d.cash_flow !== 0
            ? (d.fwd * d.mkt_ratio * d.price_change + d.mkt_basis) * d.cash_flow
            : 0;
        })) /
      forecastQ;
  }

  // FIXED ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ====
  let fixedBasis =
    (sign *
      sum(v, function(d) {
        return d.balance !== 0 && d.balance != null ? d.base * d.balance : 0;
      })) /
    basisQ;
  let fixedPRatio =
    (sign *
      sum(v, function(d) {
        return d.balance !== 0 && d.balance != null ? d.ratio * d.balance : 0;
      })) /
    basisQ;
  let fixedPFut =
    (sign *
      (sum(v, function(d) {
        return d.fwd_hedge ? d.fwd * d.hedgeNotional : 0;
      }) +
        sum(v, function(d) {
          return d.hedge_value / d.is_cents;
        }))) /
    (sign *
      sum(v, function(d) {
        return d.fwd_hedge ? d.hedgeNotional : 0;
      }));
  fixedPFut = Number.isFinite(fixedPFut) ? fixedPFut : NaN;
  let basisPFut =
    (sign *
      (sum(v, function(d) {
        return d.balance !== 0
          ? d.fwd * d.balance * d.unit_change * d.ratio
          : 0;
      }) +
        sum(v, function(d) {
          return d.hedge_value / d.is_cents;
        }))) /
    (sign *
      sum(v, function(d) {
        return d.balance !== 0 ? d.balance * d.unit_change * d.ratio : 0;
      }));
  basisPFut = Number.isFinite(basisPFut) ? basisPFut : NaN;
  let fixedP =
    (sign *
      (sum(v, function(d) {
        return d.fwd_hedge
          ? ((d.fwd + d.bonus) * (d.ratio + d.percent_bonus) * d.price_change +
              (d.base + d.freight)) *
              (d.hedgeNotional / d.ratio / d.unit_change)
          : 0;
      }) +
        sum(v, function(d) {
          return d.hedge_value;
        }))) /
    fixedHedged;
  fixedP = Number.isFinite(fixedP) ? fixedP : NaN;
  let basisP =
    (sign *
      (sum(v, function(d) {
        return d.balance !== 0
          ? ((d.fwd + d.bonus) * (d.ratio + d.percent_bonus) * d.price_change +
              (d.base + d.freight)) *
              d.balance
          : 0;
      }) +
        sum(v, function(d) {
          return d.hedge_value;
        }))) /
    basisQ;
  basisP = Number.isFinite(basisP) ? basisP : NaN;

  // NET ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ====
  let netBasis =
    basisQ >= forecastQ
      ? fixedBasis
      : weiAve(fixedBasis, basisQ, marketBasis, forecastQ);
  let netPRatio =
    basisQ >= forecastQ
      ? fixedPRatio
      : weiAve(fixedPRatio, basisQ, marketPRatio, forecastQ);
  let netPFut =
    basisQ >= forecastQ
      ? basisPFut
      : basisQ <= fixedHedged
      ? weiAve(fixedPFut, fixedHedged, marketPFut, forecastQ)
      : weiAve(basisPFut, basisQ, marketPFut, forecastQ);

  let netP;
  if (hedgeValue !== 0 && isNaN(fixedP) && isNaN(basisP)) {
    netP = (marketP * forecastQ + sign * hedgeValue) / forecastQ;
  } else {
    netP =
      basisQ >= forecastQ
        ? basisP
        : basisQ < fixedHedged
        ? weiAve(fixedP, fixedHedged, marketP, forecastQ)
        : weiAve(basisP, basisQ, marketP, forecastQ);
  }

  // ==== FUTURES + RATIO ==== ==== ==== ==== ==== ==== ==== ====
  let budgetPFutRatio = budgetPFut * budgetPRatio;
  let fixedPFutRatio = fixedP - fixedBasis; // fixedPFut * fixedPRatio;
  let basisPFutRatio = basisP - fixedBasis; // fixedPFut * fixedPRatio;
  let marketPFutRatio = marketPFut * marketPRatio;
  let netPFutRatio = netP - netBasis; // netPFut * netPRatio;
  let fixedPFutRatioPerc = formatPercentSm(
    fixedPFutRatio / budgetPFutRatio - 1
  );
  let marketPFutRatioPerc = formatPercentSm(
    marketPFutRatio / budgetPFutRatio - 1
  );
  let netPFutRatioPerc = formatPercentSm(netPFutRatio / budgetPFutRatio - 1);

  // TOTAL VALUE ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ====
  let v_hedge = sign * (fixedPFut - marketPFut) * fixedHedgedFutures; // Futures
  let v_basis = sign * (fixedBasis - marketBasis) * basisQ; // Basis
  let v_ratio = sign * (fixedPRatio - marketPRatio) * basisQ * marketPFut; // Ratio
  let v_hedge_ratio =
    sign *
    ((basisQ >= fixedHedged ? basisPFutRatio : fixedPFutRatio) -
      marketPFutRatio) *
    Math.max(basisQ, fixedHedged); // sign * (basisPFut - marketPFut) * fixedHedged; // Futures Ratio
  let v_p =
    sign *
    ((basisQ >= fixedHedged ? basisP : fixedP) - marketP) *
    Math.max(basisQ, fixedHedged); // Futures: Ratio: Basis

  // CHANGES ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ====
  // ==== QUANTITIES ==== ==== ==== ==== ==== ==== ==== ====
  let budgetQNorm = budgetQ / forecastQ;
  let forecastQNorm = 1;
  let ratioQNorm = basisQ / forecastQ;
  let fixedQNorm = fixedQ / forecastQ;
  let hedgedQNorm = hedgedQ / forecastQ;
  let fixedHedgedNorm = fixedQNorm + hedgedQNorm;
  let budgetQPerc =
    Math.round(budgetQ - forecastQ) +
    " | " +
    formatPercentSm(budgetQ / forecastQ);
  let ratioQPerc =
    Math.round(basisQ - forecastQ) +
    " | " +
    formatPercentSm(basisQ / forecastQ);
  let ratioQPercS = formatPercentSm(basisQ / forecastQ);
  let fixedQPerc =
    Math.round(fixedQ - forecastQ) +
    " | " +
    formatPercentSm(fixedQ / forecastQ);
  let fixedQPercS = formatPercentSm(fixedQ / forecastQ);
  let hedgedQPerc =
    Math.round(hedgedQ + fixedQ - forecastQ) +
    " | " +
    formatPercentSm((hedgedQ + fixedQ - forecastQ) / forecastQ);
  // ==== BASIS ==== ==== ==== ==== ==== ==== ==== ====
  let fixedBasisPerc = formatPercentSm(fixedP / budgetP - 1);
  let marketBasisPerc = formatPercentSm(fixedP / budgetP - 1);
  let netBasisPerc = formatPercentSm(fixedP / budgetP - 1);
  // ==== RATIOS ==== ==== ==== ==== ==== ==== ==== ====
  let fixedPRatioPerc = formatPercentSm(fixedPRatio / budgetPRatio - 1);
  let marketPRatioPerc = formatPercentSm(marketPRatio / budgetPRatio - 1);
  let netPRatioPerc = formatPercentSm(netPRatio / budgetPRatio - 1);
  // ==== FUTURES ==== ==== ==== ==== ==== ==== ==== ====
  let fixedPFutPerc = formatPercentSm(fixedPFut / budgetPFut - 1);
  let marketPFutPerc = formatPercentSm(marketPFut / budgetPFut - 1);
  let netPFutPerc = formatPercentSm(netPFut / budgetPFut - 1);
  // ==== PRICES ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ====
  let hValue =
    sign *
    sum(v, function(d) {
      return !isFixationDerivative(d.derivative) ? d.hedge_value : 0;
    });
  let fixedPPerc = formatPercentSm(fixedP / budgetP - 1);
  let marketPPerc = formatPercentSm(marketP / budgetP - 1);
  let netPPerc = formatPercentSm(netP / budgetP - 1);

  return {
    date: date,
    hedgeValue: hedgeValue,
    v_hedge: v_hedge,
    v_basis: v_basis,
    v_ratio: v_ratio,
    v_hedge_ratio: v_hedge_ratio,
    v_p: v_p,
    budgetQ: budgetQ,
    forecastQ: forecastQ,
    ratioQ: basisQ,
    fixedQ: fixedQ,
    q_budget: budgetQ,
    q_forecast: forecastQ,
    q_forecastPerc: forecastQNorm,
    q_ratio: basisQ,
    q_fixed: fixedQ,
    q_hedged: hedgedQ,
    q_budgetPerc: budgetQNorm,
    q_ratioPerc: ratioQNorm,
    q_ratioPercS: ratioQPercS,
    q_fixedPerc: fixedQNorm,
    q_fixedPercS: fixedQPercS,
    q_hedgedPerc: hedgedQNorm,
    p_forecast: budgetP,
    p_market: marketP,
    p_ratio: fixedP,
    p_fixed: fixedP,
    p_net: netP,
    p_fixedPerc: fixedPPerc,
    p_ratioPerc: fixedPPerc,
    p_marketPerc: marketPPerc,
    p_netPerc: netPPerc,
    fixedHedgedPerc: fixedHedgedNorm,
    fixedHedged: fixedHedged,
    hedgedQ: hedgedQ,
    budgetQNorm: budgetQNorm,
    forecastQNorm: forecastQNorm,
    ratioQNorm: ratioQNorm,
    fixedQNorm: fixedQNorm,
    hedgedQNorm: hedgedQNorm,
    fixedHedgedNorm: fixedHedgedNorm,
    budgetQPerc: budgetQPerc,
    ratioQPerc: ratioQPerc,
    ratioQPercS: ratioQPercS,
    fixedQPerc: fixedQPerc,
    fixedQPercS: fixedQPercS,
    hedgedQPerc: hedgedQPerc,
    budgetP: budgetP,
    fixedP: fixedP,
    marketP: marketP,
    hValue: hValue,
    netP: netP,
    fixedPPerc: fixedPPerc,
    marketPPerc: marketPPerc,
    netPPerc: netPPerc,
    budgetPOther: budgetBasis,
    fixedPOther: fixedBasis,
    marketPOther: marketBasis,
    netPOther: netBasis,
    fixedPOtherPerc: fixedBasisPerc,
    marketPOtherPerc: marketBasisPerc,
    netPOtherPerc: netBasisPerc,
    budgetPRatio: budgetPRatio,
    fixedPRatio: fixedPRatio,
    marketPRatio: marketPRatio,
    netPRatio: netPRatio,
    fixedPRatioPerc: fixedPRatioPerc,
    marketPRatioPerc: marketPRatioPerc,
    netPRatioPerc: netPRatioPerc,
    budgetPFut: budgetPFut,
    fixedPFut: fixedPFut,
    marketPFut: marketPFut,
    netPFut: netPFut,
    fixedPFutPerc: fixedPFutPerc,
    marketPFutPerc: marketPFutPerc,
    netPFutPerc: netPFutPerc,
    budgetPFutRatio: budgetPFutRatio,
    fixedPFutRatio: fixedPFutRatio,
    marketPFutRatio: marketPFutRatio,
    netPFutRatio: netPFutRatio,
    fixedPFutRatioPerc: fixedPFutRatioPerc,
    marketPFutRatioPerc: marketPFutRatioPerc,
    netPFutRatioPerc: netPFutRatioPerc,
    zeroFld: 0
  };
}

export function zoomedTotals(data) {
  // function aveFct(array) {
  //   return array.reduce((a, b) => a + b) / array.length;
  // }
  let len = data.length - 1;
  let date = "date...";
  if (len > -1) {
    if (Object.keys(data[0]).indexOf("date") !== -1) {
      date = dateRangeToString(data[0].date, data[len].date, true, "\u2192");
    }
  }
  // QUANTITIES ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ====
  let forecastQ = data.map(d => nonNans(d.forecastQ)).reduce(add, 0);
  let budgetQ = data.map(d => nonNans(d.budgetQ)).reduce(add, 0);
  let basisQ = data.map(d => nonNans(d.ratioQ)).reduce(add, 0);
  let fixedQ = data.map(d => nonNans(d.fixedQ)).reduce(add, 0);
  let fixedHedged = data.map(d => nonNans(d.fixedHedged)).reduce(add, 0);
  let hedgedQ = fixedHedged - fixedQ;
  let v_hedge = data.map(d => nonNans(d.v_hedge)).reduce(add, 0);
  let v_basis = data.map(d => nonNans(d.v_basis)).reduce(add, 0);
  let v_ratio = data.map(d => nonNans(d.v_ratio)).reduce(add, 0); // aveFct(data.map(d => nonNans(d.v_ratio)));
  let v_hedge_ratio = data.map(d => nonNans(d.v_hedge_ratio)).reduce(add, 0);
  let v_p = data.map(d => nonNans(d.v_p)).reduce(add, 0);

  // BUDGET ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ====
  let budgetBasis =
    data.map(d => nonNans(d.budgetPOther * d.budgetQ)).reduce(add, 0) / budgetQ;
  let budgetPRatio =
    data.map(d => nonNans(d.budgetPRatio * d.budgetQ)).reduce(add, 0) / budgetQ;
  let budgetPFut =
    data.map(d => nonNans(d.budgetPFut * d.budgetQ)).reduce(add, 0) / budgetQ;
  let budgetP =
    data.map(d => nonNans(d.budgetP * d.budgetQ)).reduce(add, 0) / budgetQ;

  // MARKET ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ====
  let marketBasis =
    data.map(d => nonNans(d.marketPOther * d.forecastQ)).reduce(add, 0) /
    forecastQ;
  let marketPRatio =
    data.map(d => nonNans(d.marketPRatio * d.forecastQ)).reduce(add, 0) /
    forecastQ;
  let marketPFut =
    data.map(d => nonNans(d.marketPFut * d.forecastQ)).reduce(add, 0) /
    forecastQ;
  let marketP =
    data.map(d => nonNans(d.marketP * d.forecastQ)).reduce(add, 0) / forecastQ;

  // FIXED ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ====
  let fixedBasis =
    data.map(d => nonNans(d.fixedPOther * d.ratioQ)).reduce(add, 0) / basisQ;
  let fixedPRatio =
    data.map(d => nonNans(d.fixedPRatio * d.ratioQ)).reduce(add, 0) / basisQ;
  let fixedPFut =
    data.map(d => nonNans(d.fixedPFut * d.fixedQ)).reduce(add, 0) / fixedQ;
  let fixedP =
    data.map(d => nonNans(d.fixedP * d.fixedHedged)).reduce(add, 0) /
    fixedHedged;

  // NET ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ====
  let netBasis =
    data
      .map(d => nonNans(d.netPOther * Math.max(d.ratioQ, d.forecastQ)))
      .reduce(add, 0) /
    data.map(d => nonNans(Math.max(d.ratioQ, d.forecastQ))).reduce(add, 0);

  let netPRatio =
    data
      .map(d => nonNans(d.netPRatio * Math.max(d.ratioQ, d.forecastQ)))
      .reduce(add, 0) /
    data.map(d => nonNans(Math.max(d.ratioQ, d.forecastQ))).reduce(add, 0);
  let netPFut =
    data
      .map(d => nonNans(d.netPFut * Math.max(d.ratioQ, d.forecastQ)))
      .reduce(add, 0) /
    data.map(d => nonNans(Math.max(d.ratioQ, d.forecastQ))).reduce(add, 0);
  let netP =
    data
      .map(d => nonNans(d.netP * Math.max(d.ratioQ, d.forecastQ)))
      .reduce(add, 0) /
    data.map(d => nonNans(Math.max(d.ratioQ, d.forecastQ))).reduce(add, 0);

  // CHANGES ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ====
  // ==== QUANTITIES ==== ==== ==== ==== ==== ==== ==== ====
  let budgetQNorm = budgetQ / forecastQ;
  let forecastQNorm = 1;
  let ratioQNorm = basisQ / forecastQ;
  let fixedQNorm = fixedQ / forecastQ;
  let hedgedQNorm = hedgedQ / forecastQ;
  let fixedHedgedNorm = fixedQNorm + hedgedQNorm;
  let budgetQPerc =
    Math.round(budgetQ - forecastQ) +
    " | " +
    formatPercentSm(budgetQ / forecastQ);
  let ratioQPerc =
    Math.round(basisQ - forecastQ) +
    " | " +
    formatPercentSm(basisQ / forecastQ);
  let ratioQPercS = formatPercentSm(basisQ / forecastQ);
  let fixedQPerc =
    Math.round(fixedQ - forecastQ) +
    " | " +
    formatPercentSm(fixedQ / forecastQ);
  let fixedQPercS = formatPercentSm(fixedQ / forecastQ);
  let hedgedQPerc =
    Math.round(hedgedQ + fixedQ - forecastQ) +
    " | " +
    formatPercentSm((hedgedQ + fixedQ - forecastQ) / forecastQ);
  // ==== BASIS ==== ==== ==== ==== ==== ==== ==== ====
  let fixedBasisPerc = formatPercentSm(fixedP / budgetP - 1);
  let marketBasisPerc = formatPercentSm(fixedP / budgetP - 1);
  let netBasisPerc = formatPercentSm(fixedP / budgetP - 1);
  // ==== RATIOS ==== ==== ==== ==== ==== ==== ==== ====
  let fixedPRatioPerc = formatPercentSm(fixedPRatio / budgetPRatio - 1);
  let marketPRatioPerc = formatPercentSm(marketPRatio / budgetPRatio - 1);
  let netPRatioPerc = formatPercentSm(netPRatio / budgetPRatio - 1);
  // ==== FUTURES ==== ==== ==== ==== ==== ==== ==== ====
  let fixedPFutPerc = formatPercentSm(fixedPFut / budgetPFut - 1);
  let marketPFutPerc = formatPercentSm(marketPFut / budgetPFut - 1);
  let netPFutPerc = formatPercentSm(netPFut / budgetPFut - 1);
  // ==== PRICES ==== ==== ==== ==== ==== ==== ==== ==== ==== ==== ====
  let hValue = data.map(d => nonNans(d.hValue)).reduce(add, 0);
  let fixedPPerc = formatPercentSm(fixedP / budgetP - 1);
  let marketPPerc = formatPercentSm(marketP / budgetP - 1);
  let netPPerc = formatPercentSm(netP / budgetP - 1);
  // ==== FUTURES + RATIO ==== ==== ==== ==== ==== ==== ==== ====
  let budgetPFutRatio = budgetPFut * budgetPRatio;
  let fixedPFutRatio = fixedPFut * fixedPRatio;
  let marketPFutRatio = marketPFut * marketPRatio;
  let netPFutRatio = netPFut * netPRatio;
  let fixedPFutRatioPerc = formatPercentSm(
    fixedPFutRatio / budgetPFutRatio - 1
  );
  let marketPFutRatioPerc = formatPercentSm(
    marketPFutRatio / budgetPFutRatio - 1
  );
  let netPFutRatioPerc = formatPercentSm(netPFutRatio / budgetPFutRatio - 1);

  return {
    date: date,
    v_hedge: v_hedge,
    v_basis: v_basis,
    v_ratio: v_ratio,
    v_p: v_p,
    v_hedge_ratio: v_hedge_ratio,
    budgetQ: budgetQ,
    forecastQ: forecastQ,
    ratioQ: basisQ,
    fixedQ: fixedQ,
    q_budget: budgetQ,
    q_forecast: forecastQ,
    q_forecastPerc: forecastQNorm,
    q_ratio: basisQ,
    q_fixed: fixedQ,
    q_hedged: hedgedQ,
    q_budgetPerc: budgetQNorm,
    q_ratioPerc: ratioQNorm,
    q_ratioPercS: ratioQPercS,
    q_fixedPerc: fixedQNorm,
    q_fixedPercS: fixedQPercS,
    q_hedgedPerc: hedgedQNorm,
    p_forecast: budgetP,
    p_market: marketP,
    p_fixed: fixedP,
    p_ratio: fixedP,
    p_net: netP,
    p_fixedPerc: fixedPPerc,
    p_ratioPerc: fixedPPerc,
    p_marketPerc: marketPPerc,
    p_netPerc: netPPerc,
    fixedHedgedPerc: fixedHedgedNorm,
    fixedHedged: fixedHedged,
    hedgedQ: hedgedQ,
    budgetQNorm: budgetQNorm,
    forecastQNorm: forecastQNorm,
    ratioQNorm: ratioQNorm,
    fixedQNorm: fixedQNorm,
    hedgedQNorm: hedgedQNorm,
    fixedHedgedNorm: fixedHedgedNorm,
    budgetQPerc: budgetQPerc,
    ratioQPerc: ratioQPerc,
    ratioQPercS: ratioQPercS,
    fixedQPerc: fixedQPerc,
    fixedQPercS: fixedQPercS,
    hedgedQPerc: hedgedQPerc,
    budgetP: budgetP,
    fixedP: fixedP,
    marketP: marketP,
    hValue: hValue,
    netP: netP,
    fixedPPerc: fixedPPerc,
    marketPPerc: marketPPerc,
    netPPerc: netPPerc,
    budgetPOther: budgetBasis,
    fixedPOther: fixedBasis,
    marketPOther: marketBasis,
    netPOther: netBasis,
    fixedPOtherPerc: fixedBasisPerc,
    marketPOtherPerc: marketBasisPerc,
    netPOtherPerc: netBasisPerc,
    budgetPRatio: budgetPRatio,
    fixedPRatio: fixedPRatio,
    marketPRatio: marketPRatio,
    netPRatio: netPRatio,
    fixedPRatioPerc: fixedPRatioPerc,
    marketPRatioPerc: marketPRatioPerc,
    netPRatioPerc: netPRatioPerc,
    budgetPFut: budgetPFut,
    fixedPFut: fixedPFut,
    marketPFut: marketPFut,
    netPFut: netPFut,
    fixedPFutPerc: fixedPFutPerc,
    marketPFutPerc: marketPFutPerc,
    netPFutPerc: netPFutPerc,
    budgetPFutRatio: budgetPFutRatio,
    fixedPFutRatio: fixedPFutRatio,
    marketPFutRatio: marketPFutRatio,
    netPFutRatio: netPFutRatio,
    fixedPFutRatioPerc: fixedPFutRatioPerc,
    marketPFutRatioPerc: marketPFutRatioPerc,
    netPFutRatioPerc: netPFutRatioPerc
  };
}

export function isFixationDerivative(derivative) {
  return derivative === "forward" || derivative === "future";
}

export function includesNoNull(str, subStr) {
  return str != null ? str.toLowerCase().includes(subStr) : false;
}

export function weiAve(p, q, p2, qTot) {
  return (p * q + p2 * (qTot - q)) / qTot;
}

export function nestCommoditiesFct(data, intervalGroup) {
  data = sortByKey(data, "date");
  let sign = Math.sign(
    sum(data, function(d) {
      return d.cash_flow;
    })
  );
  sign = sign === 0 ? 1 : sign;
  return nest()
    .key(function(d) {
      return timeComparison(d.date, intervalGroup, data);
    })
    .rollup(function(v) {
      return nestingMainCalcs(v, intervalGroup, data, sign);
    })
    .entries(data)
    .map(function(d) {
      return d.value;
    });
}

// === Create all data analysis scenarios
function createDataAnalysisScenarios({
  data,
  date,
  analysisFieldName,
  valueQty,
  calculateStrike = true,
  calculateDelta = false,
  ignore_field = null,
  ignore_values = []
}) {
  const uniqueAnalysisFieldValues = [];
  data.forEach(d => {
    if (
      d[analysisFieldName] &&
      !uniqueAnalysisFieldValues.includes(d[analysisFieldName])
    ) {
      if (ignore_field == null) {
        uniqueAnalysisFieldValues.push(d[analysisFieldName]);
      } else {
        if (!ignore_values.some(str => d[ignore_field].includes(str))) {
          uniqueAnalysisFieldValues.push(d[analysisFieldName]);
        }
      }
    }
  });
  const analysisResults = { date };

  for (const fieldValue of uniqueAnalysisFieldValues) {
    analysisResults["date"] = date;
    analysisResults["unit"] = data.filter(d => d.unit).find(d => d)?.unit;
    analysisResults["sUnit"] = data.filter(d => d.curr).find(d => d)?.curr;
    let fieldValueLowerCase = fieldValue.toLowerCase();

    const derivative2 = fieldValueLowerCase.includes("long future")
      ? "long forward"
      : fieldValueLowerCase.includes("short future")
      ? "short forward"
      : "";

    if (calculateStrike) {
      // average prices
      analysisResults[`p_${fieldValueLowerCase}`] = averageAnalysis({
        data,
        analysisType: fieldValue,
        multiplyField: "strike",
        isMultiplyField: true,
        derivative2
      });
      // }
    }
    // average notional - delta
    if (calculateDelta) {
      const dataAve = averageAnalysis({
        data,
        analysisType: fieldValue,
        multiplyField: "delta",
        isMultiplyField: true,
        calculateDelta
      });

      analysisResults[`notional_${fieldValueLowerCase}`] = dataAve[0];
      analysisResults[`delta_${fieldValueLowerCase}`] = dataAve[1];
    }
    // Sum quantities
    analysisResults[`q_${fieldValueLowerCase}`] = sumAnalysis({
      data,
      analysisType: fieldValue,
      valueAnalysis: valueQty,
      nameAnalysis: analysisFieldName,
      derivative2
    });
  }
  return analysisResults;
}
// Nest analysis scenarios
function getDataNestAnalysisScenarios({
  _this,
  intervalGroup,
  nameData,
  analysisFieldName,
  nameQty,
  valueQty,
  filterData,
  calculateStrike = true,
  calculateDelta = false,
  ignore_field = null,
  ignore_values = []
}) {
  let data = sortByKey(_this.rawData[filterData], "date");
  data = extendWeekData({
    data,
    _this,
    intervalGroup,
    nameData,
    analysisFieldName,
    valueQty,
    calculateStrike,
    calculateDelta
  });

  const nestedData = nest()
    .key(function(d) {
      return timeComparison(d.date, intervalGroup, data);
    })
    .rollup(function(v) {
      let date = timeComparison(values(v).pop().date, intervalGroup, data);
      return createDataAnalysisScenarios({
        data: v,
        date,
        analysisFieldName,
        nameQty,
        valueQty,
        calculateStrike,
        calculateDelta,
        ignore_field,
        ignore_values
      });
    })
    .entries(data)
    .map(d => d.value);

  const updateData = { [nameData]: nestedData };
  Object.assign(_this.rawData, updateData);
  Object.assign(_this.loading, { [nameData]: false });
  Object.assign(_this.rawData, {
    [`total${nameData}`]: analysisScenariosTotals(nestedData)
  });
  return nestedData;
}
// Sum analysis scenarios
function sumAnalysis({
  data,
  analysisType,
  valueAnalysis = "notional",
  nameAnalysis = "derivative_name",
  multiplyField,
  isMultiplyField = null,
  derivative2 = ""
}) {
  const sumAnalysis = sum(data, function(d) {
    let valueForAnalysis = d[valueAnalysis];
    if (isMultiplyField) {
      valueForAnalysis = d[multiplyField] * d[valueAnalysis];
    }
    return d[nameAnalysis] === analysisType || d[nameAnalysis] === derivative2
      ? valueForAnalysis
      : 0;
  });
  return Math.abs(sumAnalysis);
}
// Average analysis
function averageAnalysis({
  data,
  analysisType,
  multiplyField,
  value1Analysis = "notional",
  nameAnalysis = "derivative_name",
  derivative2 = "",
  isMultiplyField = null,
  value2Analysis = value1Analysis,
  calculateDelta = false
}) {
  const value1 = sumAnalysis({
    data,
    analysisType,
    valueAnalysis: value1Analysis,
    nameAnalysis,
    derivative2,
    multiplyField,
    isMultiplyField
  });
  const value2 = sumAnalysis({
    data,
    analysisType,
    valueAnalysis: value2Analysis,
    nameAnalysis,
    derivative2
  });
  const average = value1 / value2;
  return calculateDelta ? [value2, average] : average;
}

function calculateSum(data, key) {
  return data.map(item => nonNans(item[key])).reduce(add, 0);
}

function findUniqueKeys(data) {
  const uniqueKeys = [];
  data.forEach(item => {
    const itemKeys = Object.keys(item);
    itemKeys.forEach(key => {
      let isPrices = key.startsWith("p_");
      const isPriceKey = key.startsWith(isPrices ? "p_" : "q_");
      if (!uniqueKeys.includes(key) && isPriceKey && !key.endsWith("Perc")) {
        uniqueKeys.push(key);
      }
    });
  });
  return uniqueKeys;
}

function getTotalPrices(data, dataKeys) {
  const resultPrices = {};
  dataKeys.forEach(key => {
    const total = calculateSum(data, key);
    const count = data.filter(item => !isNaN(item[key])).length;
    resultPrices[key] = count > 0 ? total / count : 0;
  });
  return resultPrices;
}

function getAveragesQty(data, dataKeys) {
  let sumTotals = 0;
  const resultQty = {};
  dataKeys.forEach(key => {
    resultQty[key] = calculateSum(data, key);
    sumTotals += resultQty[key];
  });
  for (const key in resultQty) {
    resultQty[`${key}Perc`] = resultQty[key] / sumTotals;
  }
  return resultQty;
}

function analysisScenariosTotals(data) {
  const date =
    data.length > 0 && "date" in data[0]
      ? dateRangeToString(
          data[0].date,
          data[data.length - 1].date,
          true,
          "\u2192"
        )
      : "date...";

  const uniqueKeys = findUniqueKeys(data);
  const resultPrices = getTotalPrices(
    data,
    uniqueKeys.filter(d => d.startsWith("p_"))
  );
  const resultQty = getAveragesQty(
    data,
    uniqueKeys.filter(d => d.startsWith("q_"))
  );

  return [
    {
      date,
      ...resultPrices,
      ...resultQty
    }
  ];
}

function extendWeekData({ _this, data }) {
  let dateList;
  const initialDate = _this["rawData"]["zoomedData"][0].date;
  const finalDate = _this["rawData"]["zoomedData"].at(-1).date;
  dateList = _this.processData.createWeekByWeekDays(initialDate, finalDate);

  const rawDataExtended = [];
  dateList.forEach(d =>
    rawDataExtended.push({
      date: d,
      buy_sell: "long",
      derivative: "forward",
      derivative_name: "long forward",
      strike: NaN,
      notional: NaN,
      unit: "",
      curr: "",
      data: []
    })
  );
  data = data.concat(rawDataExtended);

  const sortedData = sortByKey(data, "date");
  return sortedData;
}

/**
 * Create dates for nesting
 */
export function timeComparison(date, intervalGroup, data) {
  // Nesting function, returns the appropriate date for nesting
  let dateCopy;
  let durfn;
  if (intervalGroup === "1D") {
    durfn = timeDay(date);
  } else if (intervalGroup === "1W") {
    durfn = timeMonday(date);
  } else if (intervalGroup === "2W") {
    dateCopy = timeDay(
      timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(JSON.parse(JSON.stringify(date)))
    );
    if (date.getDate() < 15) {
      durfn = timeDay(dateCopy.setDate(1));
    } else {
      durfn = timeDay(dateCopy.setDate(15));
    }
  } else if (intervalGroup === "1M") {
    durfn = timeMonth(date);
  } else if (intervalGroup === "1Q") {
    durfn = quarterDate(date);
  } else if (intervalGroup === "6M") {
    dateCopy = timeDay(
      timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(JSON.parse(JSON.stringify(date)))
    );
    if (dateCopy.getMonth() < 6) {
      durfn = timeDay(timeDay(dateCopy.setMonth(0)).setDate(1));
    } else {
      durfn = timeDay(timeDay(dateCopy.setMonth(6)).setDate(1));
    }
  } else if (intervalGroup === "1Y") {
    if (date.getFullYear() === data[0].date.getFullYear()) {
      durfn = timeDay(
        timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(
          JSON.parse(JSON.stringify(data[0].date))
        )
      );
    } else {
      durfn = timeYear(date);
    }
  } else if (intervalGroup === "all") {
    durfn = timeDay(
      timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(
        JSON.parse(JSON.stringify(data[0].date))
      )
    );
  }
  return durfn;
}

export function quarterDate(date) {
  // Return the quarter that the variable "date" belongs to, for nesting
  let tod = new Date();
  let mDiff = monthDiff(tod, date);
  let newDate;
  if (mDiff === 0) {
    newDate = timeMonth(date);
  } else {
    let quarters = Math.floor(monthDiff(tod, date) / 3);
    newDate = timeDay(
      timeDay(tod.setMonth(tod.getMonth() + 3 * quarters + 1)).setDate(1)
    );
  }
  return newDate;
}

export function monthDiff(d1, d2) {
  // Months between two dates
  let months;
  months = (d2.getFullYear() - d1.getFullYear()) * 12;
  months += d2.getMonth() - d1.getMonth();
  return months <= 0 ? 0 : months;
}

export function hedgeConditionNew(d) {
  return (
    d === "hedge" ||
    d === "limited_hedge" ||
    d === "hedge_and_reduce_cost" ||
    d === "ko_hedge" ||
    d === "offset_a_long_position" ||
    d === "offset_a_short_position" ||
    d === "offset a long position" ||
    d === "offset a short position"
  );
}
